import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

import {
  listBoutiques,
  createBoutiqueApi,
  updateBoutiqueApi,
  deleteBoutiqueApi,
} from "../../helpers/api/custom/boutiquesApi";

import { BoutiqueActionTypes } from "./constants";
import {
  setBoutiques,
  loadBoutiquesError,
  updateSelectedBoutique,
  createBoutiqueSuccess,
  createBoutiqueError,
  updateBoutiqueSuccess,
  updateBoutiqueError,
  deleteBoutiqueSuccess,
  deleteBoutiqueError,
} from "./actions";
import { BoutiqueItem } from "./types";
import { selectBoutiqueList } from "./selectors";

const SELECTED_BOUTIQUE_KEY = "selectedBoutiqueId";

function* loadBoutiquesSaga(): SagaIterator {
  try {
    const response = yield call(listBoutiques);
    const list: BoutiqueItem[] = response?.data?.data ?? [];
    yield put(setBoutiques(list));
    // Drop a stale selection (e.g. boutique was deleted on another device).
    const selectedRaw = localStorage.getItem(SELECTED_BOUTIQUE_KEY);
    const selected = selectedRaw ? JSON.parse(selectedRaw) : null;
    if (selected && !list.find((b) => b.id === selected)) {
      localStorage.removeItem(SELECTED_BOUTIQUE_KEY);
      yield put(updateSelectedBoutique(null));
    }
  } catch (err: any) {
    // A failed load must NOT masquerade as "0 boutiques" — that wrongly shows
    // the "create your first boutique" prompt to a seller who already has some.
    // Set an error (keeping any previously loaded list) so the page shows a
    // retry instead.
    const msg =
      err?.response?.data?.error || err?.message || "Failed to load boutiques";
    yield put(loadBoutiquesError(msg));
  }
}

function* changeBoutiqueSaga(action: { payload: string | null }): SagaIterator {
  const id = action.payload;
  // Only accept ids that exist in the current list (or null).
  if (id !== null) {
    const list: BoutiqueItem[] = yield select(selectBoutiqueList);
    if (!list.find((b) => b.id === id)) return;
  }
  if (id) {
    localStorage.setItem(SELECTED_BOUTIQUE_KEY, JSON.stringify(id));
  } else {
    localStorage.removeItem(SELECTED_BOUTIQUE_KEY);
  }
  yield put(updateSelectedBoutique(id));
}

function* createBoutiqueSaga(action: any): SagaIterator {
  try {
    const response = yield call(createBoutiqueApi, action.payload);
    const created: BoutiqueItem = response?.data?.data;
    if (!created?.id) {
      yield put(createBoutiqueError("Invalid server response"));
      return;
    }
    yield put(createBoutiqueSuccess(created));
  } catch (err: any) {
    const msg = err?.response?.data?.error || err?.message || "Create failed";
    yield put(createBoutiqueError(msg));
  }
}

function* updateBoutiqueSaga(action: any): SagaIterator {
  try {
    const { id, ...rest } = action.payload;
    const response = yield call(updateBoutiqueApi, id, rest);
    const patch = response?.data?.data ?? { id };
    yield put(updateBoutiqueSuccess({ ...patch, id }));
  } catch (err: any) {
    const msg = err?.response?.data?.error || err?.message || "Update failed";
    yield put(updateBoutiqueError(msg));
  }
}

function* deleteBoutiqueSaga(action: any): SagaIterator {
  try {
    yield call(deleteBoutiqueApi, action.payload.id);
    yield put(deleteBoutiqueSuccess(action.payload.id));
    // If the deleted one was selected, drop the filter.
    const list: BoutiqueItem[] = yield select(selectBoutiqueList);
    if (!list.find((b) => b.id === action.payload.id)) {
      localStorage.removeItem(SELECTED_BOUTIQUE_KEY);
      yield put(updateSelectedBoutique(null));
    }
  } catch (err: any) {
    const msg = err?.response?.data?.error || err?.message || "Delete failed";
    yield put(deleteBoutiqueError(msg));
  }
}

export function* watchLoadBoutiques() {
  yield takeLatest(BoutiqueActionTypes.LOAD_BOUTIQUES, loadBoutiquesSaga);
}

export function* watchChangeBoutique() {
  // @ts-expect-error redux-saga overload resolution treats enum as TakeableChannel
  yield takeLatest(BoutiqueActionTypes.CHANGE_BOUTIQUE, changeBoutiqueSaga);
}

export function* watchCreateBoutique() {
  yield takeLatest(BoutiqueActionTypes.CREATE_BOUTIQUE, createBoutiqueSaga);
}

export function* watchUpdateBoutique() {
  yield takeLatest(BoutiqueActionTypes.UPDATE_BOUTIQUE, updateBoutiqueSaga);
}

export function* watchDeleteBoutique() {
  yield takeLatest(BoutiqueActionTypes.DELETE_BOUTIQUE, deleteBoutiqueSaga);
}

function* boutiqueSaga() {
  yield all([
    fork(watchLoadBoutiques),
    fork(watchChangeBoutique),
    fork(watchCreateBoutique),
    fork(watchUpdateBoutique),
    fork(watchDeleteBoutique),
  ]);
}

export default boutiqueSaga;
